import { css } from 'aphrodite';
import * as React from 'react';
import type { ISupportTicket } from '../../../models/Support/ISupportTicket';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../web/components/DataGrid';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import { previousConversationsModalStyles as modalStyles } from './styles/previousConversationsModalStyles';
import { formatRelativeTime, getTicketAssigneeName } from './supportTicketPresentation';
import { TicketPagination } from './TicketPagination';

const CONVERSATIONS_PER_PAGE = 10;

interface PreviousConversationsModalProps {
	/** Every other ticket from this contact, already ordered newest-first by the drawer. */
	tickets: ISupportTicket[];
	contactLabel: string;
	agentNameMap: Record<string, string>;
	/** Opens the ticket preview (merge) modal for the clicked conversation. */
	onSelectTicket: (ticketId: string) => void;
	/** Owned by the drawer so the preview modal's Back button returns to the page the agent left. */
	page: number;
	onPageChange: (page: number) => void;
	onClose: () => void;
}

/** Full, paginated list of a contact's other conversations — the drawer only shows the newest few. */
export const PreviousConversationsModal: React.FC<PreviousConversationsModalProps> = ({
	tickets,
	contactLabel,
	agentNameMap,
	onSelectTicket,
	page,
	onPageChange,
	onClose,
}) => {
	const totalPages = Math.max(1, Math.ceil(tickets.length / CONVERSATIONS_PER_PAGE));
	const currentPage = Math.min(page, totalPages);
	const firstIndex = (currentPage - 1) * CONVERSATIONS_PER_PAGE;
	const visibleTickets = tickets.slice(firstIndex, firstIndex + CONVERSATIONS_PER_PAGE);

	return (
		<Dialog
			open
			onOpenChange={open => {
				if (!open) onClose();
			}}
		>
			<DialogPopup styles={[modalStyles.panel]}>
				<DialogHeader styles={[modalStyles.header]}>
					<DialogTitle styles={[modalStyles.title]}>Conversations with {contactLabel}</DialogTitle>
				</DialogHeader>

				<DialogBody styles={[modalStyles.body]}>
					<DataGrid aria-label={`Conversations with ${contactLabel}`} styles={[modalStyles.table]}>
						<DataGridColumns>
							<DataGridColumn sticky>Conversation</DataGridColumn>
							<DataGridColumn sticky styles={[modalStyles.colMessages]}>
								Messages
							</DataGridColumn>
							<DataGridColumn sticky styles={[modalStyles.colAssignee]}>
								Assignee
							</DataGridColumn>
							<DataGridColumn sticky styles={[modalStyles.colNumber]}>
								Number
							</DataGridColumn>
							<DataGridColumn sticky styles={[modalStyles.colLastUpdate]}>
								Last update
							</DataGridColumn>
						</DataGridColumns>
						<DataGridRows>
							{visibleTickets.map(ticket => (
								<DataGridRow key={ticket.id} styles={[modalStyles.row]} onClick={() => onSelectTicket(ticket.id)}>
									<DataGridRowItem>
										<span className={css(modalStyles.subject)} title={ticket.subject || undefined}>
											{ticket.subject || 'Untitled'}
										</span>
										{ticket.previewText && <span className={css(modalStyles.previewText)}>{ticket.previewText}</span>}
									</DataGridRowItem>
									<DataGridRowItem>
										<span className={css(modalStyles.messageCount)}>{ticket.threadCount}</span>
									</DataGridRowItem>
									<DataGridRowItem styles={[modalStyles.cellMuted]}>
										{getTicketAssigneeName(ticket, agentNameMap) ?? 'Unassigned'}
									</DataGridRowItem>
									<DataGridRowItem styles={[modalStyles.cellMuted]}>{ticket.number}</DataGridRowItem>
									<DataGridRowItem styles={[modalStyles.cellMuted]}>
										{formatRelativeTime(ticket.lastMessageAt)}
									</DataGridRowItem>
								</DataGridRow>
							))}
						</DataGridRows>
					</DataGrid>
				</DialogBody>

				<DialogFooter styles={[modalStyles.footer]}>
					<span className={css(modalStyles.footerCount)}>
						{tickets.length} conversations · Viewing {firstIndex + 1}-{firstIndex + visibleTickets.length}
					</span>
					<TicketPagination
						page={currentPage}
						totalPages={totalPages}
						onSelectPage={onPageChange}
						styles={[modalStyles.footerPagination]}
					/>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
